<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Operational Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Audit Trail</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Audit Trails</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewAuditTrail" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Audit Trail</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callHelp" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-question-circle mr-2"></i>
                                    <span>Help</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callSupport" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-info-circle mr-2"></i>
                                    <span>Contact Support</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable :value="auditTrails" :paginator="true"
                               class="p-datatable-gridlines"
                               :rows="100" dataKey="id" :rowsPerPageOptions="[10,20,50,100]" :rowHover="true"
                               :striped-rows="true" :reorderableColumns="true"
                               v-model:filters="filters" filterDisplay="menu" :loading="loading"
                               :filters="filters" responsiveLayout="scroll"
                               :globalFilterFields="['description', 'mainProcess', 'subProcess', 'created']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Main Process, Sub Process, Created or Description"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>
                        </template>

                        <template #empty>
                            No audit trails found.
                        </template>

                        <template #loading>
                            Loading audit trails. Please wait.
                        </template>

                        <Column field="created" header="Created" :sortable="true"/>

                        <Column v-for="col in selectedColumns"
                                :key="col.field"
                                :field="col.field"
                                :header="col.header"
                                :sortable="col.sortable" />

                        <Column field="description" header="Description" :sortable="true"/>
                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Audit Trail</h3>

                    <div class="field">
                        <label for="mainProcess">Main Process</label>
                        <AutoComplete placeholder="Search" :dropdown="false" :multiple="false"
                                      v-model="newAuditTrail.mainProcess" :suggestions="filteredProcesses"
                                      @complete="searchProcesses($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="subProcess">Sub Process</label>
                        <Dropdown v-model="newAuditTrail.subProcess" :options="mainProcessTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="description">Description</label>
                        <Textarea v-model="newAuditTrail.description" :autoResize="true"
                                  rows="10" cols="50"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createAuditTrail"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import {FilterMatchMode, FilterOperator} from 'primevue/api';
import UserService from "@/service/UserService";
import AuditTrailService from "@/service/AuditTrailService";
import ProcessService from "@/service/ProcessService";
import CompanyService from "@/service/CompanyService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        MenuHealthCompact,
        IsoEvolveFooter,
    },
    setup() {
        const userStore = useUserStore()

        console.error("[AppointmentsNew][setup] this.userStore = ", userStore);

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'mainProcess', header: 'Main Process', sortable: true},
            {field: 'subProcess', header: 'Sub Process', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'mainProcess', header: 'Main Process', sortable: true},
            {field: 'subProcess', header: 'Sub Process', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'today', header: 'Today'},
            {field: 'thisMonth', header: 'This Month'}
        ];

    },
    async mounted() {
        try {
            const userPingResponse = await UserService.getUserPing();
            console.log("[PmOverviewDashboard][mounted] System is active.");
            console.log("[PmOverviewDashboard][mounted] Data = ", userPingResponse);
        } catch (e) {
            console.log("[PmOverviewDashboard][mounted] System is NOT active.");
            console.log("[PmOverviewDashboard][mounted] Error = ", e);
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            console.log("[PmOverviewDashboard][mounted] User is NOT defined.");
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        await this.loadCompanyContext();
    },
    watch: {
        '$route.query.tab'(newVal) {
            if (newVal === "1") {
                this.activeTab = 1;
                this.$router.replace({ path: this.$route.path });
            } else if (newVal === "2") {
                this.callNewAuditTrail();
                this.$router.replace({ path: this.$route.path });
            }
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    data() {

        return {
            profile: {},
            submitted: false,
            auditTrails: null,
            originalAuditTrails: null,
            newAuditTrail: {},
            companyConfiguration: {},
            filters: null,
            loading: true,
            activeTab: 1,
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            filteredProcesses: [],
            mainProcessTypes: [
                {name: 'Internal', code: 'Internal'},
                {name: 'Prospect Management', code: 'Prospect Management'},
                {name: 'Client Management', code: 'Client Management'},
                {name: 'Unpaids Management', code: 'Unpaids Management'},
                {name: 'Invoicing', code: 'Invoicing'},
                {name: 'Calendar', code: 'Calendar'},
                {name: 'Health Insurance Claims', code: 'Health Insurance Claims'},
                {name: 'Payments', code: 'Payments'}
            ],
            subProcessTypes: [
                {name: 'Internal', code: 'Internal'},
                {name: 'Prospect Discovery', code: 'Prospect Discovery'},
                {name: 'Prospect Outreach', code: 'Prospect Outreach'},
                {name: 'Prospect Engagement', code: 'Prospect Engagement'},
                {name: 'Client Data', code: 'Client Data'},
                {name: 'Reminders', code: 'Reminders'},
                {name: 'Invoice Management', code: 'Invoice Management'},
                {name: 'Payment Management', code: 'Payment Management'},
                {name: 'Claim Management', code: 'Claim Management'},
                {name: 'Unpaids Management', code: 'Unpaids Management'}
            ]
        }
    },
    methods: {
        async loadCompanyContext() {
            console.log("[PmOverviewDashboard][mounted] User is defined. this.userStore.user.id = ", this.userStore.user.id);

            console.log("[AuditTrail][mounted] this.userStore = ", this.userStore);

            try {
                try {
                    // Attempt to get user data
                    const userData = await UserService.getUser(this.userStore.user.id);
                    this.profile = userData.data;
                } catch (e) {
                    console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                    this.userStore.clearUser();
                    this.$router.push('login');
                    return;
                }

                this.profile.companyId = this.userStore.activeCompanyId;

                const [companyConfigurationData, auditTrailsData] = await Promise.all([
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    AuditTrailService.getCompanyAuditTrails(this.profile.companyId)
                ]);

                this.companyConfiguration = companyConfigurationData.data;

                this.auditTrails = auditTrailsData.data;
                this.originalAuditTrails = [...this.auditTrails];

                console.log("[AuditTrail][mounted] this.auditTrails = ", this.auditTrails);

                this.loading = false;

            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching additional data = ", e);
            }

            if (this.$route.query.tab === "1") {
                this.activeTab = 1;
                this.$router.replace({ path: this.$route.path });
            }

            if (this.$route.query.tab === "2") {
                this.callNewAuditTrail();
                this.$router.replace({ path: this.$route.path });
            }

            console.log("[AppointmentsNew][mounted] this.auditTrails = ", this.auditTrails);
            console.log("[AppointmentsNew][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        callReset() {
            this.activeTab = 1;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        callNewAuditTrail() {
            this.activeTab = 2;
            this.newAuditTrail = {};
            this.$router.replace({ path: this.$route.path });
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'mainProcess': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'subProcess': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'created': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        restoreToOriginal() {
            window.location.reload();
        },
        clearFilter() {
            this.initFilters();
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isTodaySelected = value.some(item => item.field === "today");
            const isThisMonthSelected = value.some(item => item.field === "thisMonth");

            if (this.auditTrails !== null) {

                if (isTodaySelected) {
                    // The filter "today" is present in selectedStandardFilters
                    console.log("The filter 'today' is selected.");

                    const today = new Date();
                    const year = today.getFullYear();
                    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based in JS
                    const day = String(today.getDate()).padStart(2, '0');
                    const todayStr = `${year}-${month}-${day}`;

                    this.auditTrails = this.originalAuditTrails.filter(auditTrail => {
                        // Assuming 'created' is in 'YYYY-MM-DD' format
                        return auditTrail.created.startsWith(todayStr);
                    });

                } else if (isThisMonthSelected) {
                    console.log("The filter 'thisMonth' is selected.");

                    const today = new Date();
                    const year = today.getFullYear();
                    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based in JS
                    const monthStr = `${year}-${month}`;

                    this.auditTrails = this.originalAuditTrails.filter(auditTrail => {
                        // Assuming 'created' is in 'YYYY-MM-DD' format
                        return auditTrail.created.startsWith(monthStr);
                    });

                } else {
                    // The filter "today" is not present in selectedStandardFilters
                    console.log("No filters were selected.");
                    this.auditTrails = this.originalAuditTrails;
                }

            } else {
                // Handle the case when auditTrails is null
                this.auditTrails = null;
            }
        },
        convertDateIntoLocalTimezone(inputDate) {
            console.log("[convertDateIntoLocalTimezone] inputDate :", inputDate);

            try {
                const moment = require("moment-timezone");
                const inputTimeZone = "Etc/UTC"; // Assuming the input date is in UTC

                let targetTimeZone = "Europe/London";

                if (this.companyConfiguration.region == "ZA") {
                    targetTimeZone = "Africa/Johannesburg";
                }

                console.log("[convertDateIntoLocalTimezone] targetTimeZone :", targetTimeZone);

                // Parse the input date string and set the input time zone
                const parsedDate = moment.tz(inputDate, "YYYY-MM-DD HH:mm:ss", inputTimeZone);

                console.log("[convertDateIntoLocalTimezone] parsedDate :", parsedDate);

                // Convert the date to the target time zone
                const outputDate = parsedDate.clone().tz(targetTimeZone);

                console.log("[convertDateIntoLocalTimezone] Output Date :", outputDate.format("YYYY-MM-DD HH:mm:ss"));

                return outputDate.format("YYYY-MM-DD HH:mm:ss");

            } catch (error) {
                console.error("[convertDateIntoLocalTimezone] Could not convert date. Returning input date. Error :", error);

                return inputDate;
            }

        },
        async searchProcesses(event) {
            console.log("[MsTasks][searchProcesses] event.query = ", event.query);

            const [processData] = await Promise.all([
                ProcessService.searchCompanyProcesses(this.profile.companyId, event.query.toLowerCase())
            ]);

            this.filteredProcesses = processData.data;
        },
        async createAuditTrail() {
            this.submitted = true;

            if (this.newAuditTrail.description) {

                try {
                    var tempProcess = "";

                    try {
                        tempProcess = this.newAuditTrail.mainProcess.name;
                    } catch (error) {
                        tempProcess = "Internal";
                    }

                    var auditTrailDto = {
                        companyId: this.profile.companyId,
                        userId: this.userStore.user.id,
                        mainProcess: tempProcess,
                        subProcess: this.newAuditTrail.subProcess,
                        description: this.newAuditTrail.description
                    };

                    const response = await AuditTrailService.createAuditTrail(auditTrailDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [auditTrailsData] = await Promise.all([
                        AuditTrailService.getCompanyAuditTrails(this.profile.companyId)
                    ]);

                    this.auditTrails = auditTrailsData.data;
                    this.originalAuditTrails = auditTrailsData.data;
                    this.userStore.setAuditTrails(auditTrailsData.data);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created audit trail',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create audit trail. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
    }
};
</script>

<style scoped>
a {
    text-decoration: none;
}
</style>
